/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React from 'react'
import { type DonutSeriesData } from './DonutChart'

// Load required Highcharts modules at module level
// eslint-disable-next-line @typescript-eslint/no-var-requires
require('highcharts/modules/series-label')(Highcharts)

interface BubbleChartProps {
  data: DonutSeriesData
  height: number
}

const extractTrailingNumber = (label: string): number => {
  const m = label.match(/(\d+)\s*$/)
  return m ? parseInt(m[1], 10) : NaN
}

export function BubbleChart (props: BubbleChartProps): JSX.Element {
  const names = props.data.name || []
  const yVals = props.data.data || []
  const colors = props.data.color || []

  const xVals = names.map(extractTrailingNumber)
  const maxY = yVals.length ? Math.max(...yVals) : 0

  // Transform data for scatter chart with marker sizes based on percentage
  const transformedData = xVals.map((x, i) => {
    const y = yVals[i] ?? 0
    const color = colors[i] ?? '#FFFFFF'

    // Percentage of max (0..100). If maxY is 0, default to 0.
    const percentageValue = maxY > 0 ? (y / maxY) * 100 : 0

    // Radius calculation: min radius for <6%, otherwise scale from 10px to 59px width (radius = width/2)
    let radius = 5 // minimum radius for <6%
    if (percentageValue >= 6) {
      radius = 5 + ((percentageValue - 6) / 94) * 24.5 // scales up to ~29.5
    }

    return {
      x,
      y,
      marker: {
        radius,
        fillColor: color,
        lineWidth: 1.5,
        lineColor: '#FFFFFF'
      },
      tooltipValue: y,
      color
    }
  })

  const chartOptions: Highcharts.Options = {
    chart: {
      type: 'scatter',
      backgroundColor: 'transparent',
      height: props.height,
      spacing: [60, 40, 0, 10],
      borderColor: 'transparent',
      events: {
        render: function () {
          const chart = this as unknown as Highcharts.Chart
          (chart as any).__xBars?.forEach(
            (el: Highcharts.SVGElement) => {
              el.destroy()
            }
          );
          (chart as any).__xBars = []

          const xAxis = chart.xAxis[0]
          if (!xAxis) return

          const plotLeft = chart.plotLeft
          const plotTop = chart.plotTop
          const plotWidth = chart.plotWidth
          const plotHeight = chart.plotHeight

          const axisLineY = plotTop + plotHeight

          const offsetBelow = 12
          const barY = axisLineY + offsetBelow
          const barHeight = 3

          const toPx = (x: number): number => xAxis.toPixels(x, false)
          const visibleTickCenters = [7, 8, 9, 10, 11, 12]
            .map(x => ({ x, px: toPx(x) }))
            .filter(({ px }) => px >= plotLeft && px <= (plotLeft + plotWidth))

          let barWidth = 21
          if (visibleTickCenters.length > 1) {
            const gaps: number[] = []
            for (let i = 1; i < visibleTickCenters.length; i++) {
              gaps.push(visibleTickCenters[i].px - visibleTickCenters[i - 1].px)
            }
            const avgGap = gaps.reduce((a, b) => a + b, 0) / gaps.length
            barWidth = Math.max(12, Math.min(28, avgGap * 0.45))
          }

          visibleTickCenters.forEach(({ x, px }) => {
            const barX = px - barWidth / 2
            const colorMap: Record<number, string> = {
              7: '#59C400',
              8: '#B5E31E',
              9: '#FFD84A',
              10: '#FFA629',
              11: '#FF7F37',
              12: '#FF4D3A'
            }
            const color = colorMap[x]

            const rect = chart.renderer
              .rect(barX, barY, barWidth, barHeight, 0)
              .attr({
                fill: color,
                'stroke-width': 0,
                zIndex: 7
              })
              .add();

            (chart as any).__xBars.push(rect)
          })
        }
      }
    } as any,

    title: { text: undefined },
    subtitle: { text: undefined },

    xAxis: {
      type: 'linear',
      min: 6,
      max: Math.max(...(xVals.length ? xVals : [12])),
      tickInterval: 1,
      gridLineColor: '#4a4a4a',
      gridLineDashStyle: 'dash' as any,
      gridLineWidth: 1,
      labels: {
        style: {
          color: '#D0D0D0',
          fontWeight: '500',
          fontSize: '12px',
          fontFamily: 'Honeywell Sans Web, sans-serif'
        },
        y: 35
      },
      title: {
        text: 'Spread Factor',
        style: {
          color: '#F1F1F1',
          fontSize: '12px',
          fontFamily: 'Honeywell Sans Web, sans-serif'
        }
      },
      lineColor: '#555555'
    } as any,

    yAxis: {
      type: 'linear',
      min: 0,
      max: maxY > 0 ? Math.ceil(maxY * 1.1) : 2,
      allowDecimals: false,
      gridLineColor: undefined,
      labels: {
        style: {
          color: '#D0D0D0',
          fontWeight: '500',
          fontSize: '12px',
          fontFamily: 'Honeywell Sans Web, sans-serif'
        },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
          return `${this.value}`
        }
      },
      title: {
        text: 'Number of Assets',
        style: {
          color: '#F1F1F1',
          fontSize: '12px',
          fontFamily: 'Honeywell Sans Web, sans-serif'
        }
      },
      lineColor: '#555555'
    } as any,
    plotOptions: {
      scatter: {
        dataLabels: { enabled: false },
        states: {
          hover: {
            halo: {
              size: 10,
              opacity: 0.8
            }
          }
        }
      }
    },

    legend: { enabled: false },
    credits: { enabled: false },
    boost: { enabled: false },

    tooltip: {
      enabled: true,
      shared: false,
      headerFormat: '',
      pointFormat: '{point.tooltipValue}',
      backgroundColor: '#FFFFFF',
      borderColor: '#FFFFFF',
      borderRadius: 8,
      shape: 'callout',
      style: {
        color: '#000000',
        fontSize: '14px',
        fontFamily: 'Honeywell Sans Web, sans-serif'
      },
      outside: true,
      useHTML: true,
      padding: 12
    },
    exporting: { enabled: false },
    series: [
      {
        type: 'scatter' as any,
        name: 'Spread Factor',
        data: transformedData as any
      }
    ]
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      immutable={true}
    />
  )
}

export default BubbleChart
